import { memo, useCallback, useMemo, useState } from 'react';

interface DonutProps {
  /** Memoized on identity: pass a stable reference, not an inline array. */
  segments: { pct: number; color: string }[];
  /** Outer square size in px. Defaults to 108. */
  size?: number;
  /** Ring thickness in px. Defaults to 18. */
  thickness?: number;
  /** Fires with the hovered segment's index, or null on mouse-leave - lets
   *  the caller swap a center label to show that segment's own value
   *  (see AnalyticsTab's Content Types / Quality donuts). */
  onHoverChange?: (index: number | null) => void;
  /** Externally-controlled active segment (e.g. hovering the matching legend
   *  row outside the ring) - takes priority over the ring's own hover state
   *  for the dim/highlight effect, so legend and ring stay in sync either
   *  direction. */
  activeIndex?: number | null;
}

// 0deg = 3 o'clock, increasing = clockwise (SVG y grows downward); the whole
// ring is rotated -90deg below so the first segment starts at 12 o'clock.
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// One donut segment as a single filled annulus wedge (outer arc, straight
// edge in, inner arc back, close) rather than a stroked circle clipped by
// stroke-dasharray. Two adjacent dasharray circles are each anti-aliased
// independently, so their shared seam doesn't line up pixel-for-pixel and
// renders as a visible jagged notch; a filled path has no such seam because
// adjacent wedges share the exact same boundary coordinates.
function wedgePath(cx: number, cy: number, outerR: number, innerR: number, startDeg: number, endDeg: number): string {
  // A true 360deg arc has coincident start/end points, which degenerates the
  // SVG arc command - clamp a full circle just short of the wrap-around.
  const clampedEnd = endDeg - startDeg >= 359.99 ? startDeg + 359.99 : endDeg;
  const largeArc = clampedEnd - startDeg > 180 ? 1 : 0;
  const outerStart = polarToCartesian(cx, cy, outerR, startDeg);
  const outerEnd = polarToCartesian(cx, cy, outerR, clampedEnd);
  const innerEnd = polarToCartesian(cx, cy, innerR, clampedEnd);
  const innerStart = polarToCartesian(cx, cy, innerR, startDeg);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

interface DonutWedgeProps {
  index: number;
  d: string;
  color: string;
  dimmed: boolean;
  onHover: (index: number | null) => void;
}

/** Memoized so a hover re-renders two wedges, not the whole ring. */
const DonutWedge = memo(function DonutWedge({ index, d, color, dimmed, onHover }: DonutWedgeProps) {
  const handleEnter = useCallback(() => onHover(index), [index, onHover]);
  const handleLeave = useCallback(() => onHover(null), [onHover]);

  return (
    <path
      d={d}
      strokeWidth={0.5}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        fill: color,
        stroke: color,
        cursor: 'pointer',
        opacity: dimmed ? 0.35 : 1,
        transition: 'opacity 0.15s ease',
      }}
    />
  );
});

export function Donut({ segments, size = 108, thickness = 18, onHoverChange, activeIndex }: DonutProps) {
  const [internalHovered, setInternalHovered] = useState<number | null>(null);
  const hovered = activeIndex !== undefined ? activeIndex : internalHovered;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2;
  const innerR = size / 2 - thickness;

  const setHover = useCallback(
    (i: number | null) => {
      setInternalHovered(i);
      onHoverChange?.(i);
    },
    [onHoverChange],
  );

  // Cumulative sweep as a pure scan; React Compiler flags render-time mutation.
  const wedges = useMemo(() => {
    let sweptDeg = 0;
    const wedges: { d: string; color: string }[] = [];
    for (const seg of segments) {
      const endDeg = sweptDeg + (seg.pct / 100) * 360;
      wedges.push({ d: wedgePath(cx, cy, outerR, innerR, sweptDeg, endDeg), color: seg.color });
      sweptDeg = endDeg;
    }
    return wedges;
  }, [segments, cx, cy, outerR, innerR]);

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={cx}
        cy={cy}
        r={size / 2 - thickness / 2}
        strokeWidth={thickness}
        style={{ fill: 'none', stroke: 'var(--background-muted)' }}
      />
      {wedges.map((w, i) => (
        <DonutWedge
          key={i}
          index={i}
          d={w.d}
          color={w.color}
          dimmed={hovered != null && hovered !== i}
          onHover={setHover}
        />
      ))}
    </svg>
  );
}
